import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import {useIntl, FormattedMessage} from 'react-intl';
import {FlexColumn} from '@forecast-it/design-system';
import type {Message} from './useAIChatStream';
import {chatBodyFont, chatHeadingFont, D, TRUNCATE_AT, PREVIEW_CHARS} from './design-tokens';
import {MessageExpandToggle, ThinkingDots} from './icons';
import {StatusSteps} from './StatusSteps';
import {StatusCards, tryParseStatusCardTable} from './StatusCards';
import {ActionCard} from './ActionCard';
import {ReportIntentCard} from './ReportIntentCard';
import type {ActionResult, HistoryProp} from './actionCardTypes';
import {normalizeMarkdownHeadings} from './chatMarkdownUtils';
import {normalizeChatLink} from './chatLinkUtils';
import type {ReportTaxonomy} from '../ai-report-builder/contracts/report-taxonomy.types';

// ── Markdown styled components ─────────────────────────────────────────────────
// One named component per markdown element — zero inline styles in mdComponents.

const MdP = styled.p`
	${chatBodyFont}
	margin: 0 0 8px;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
`;

const MdInternalLink = styled.a`
	color: ${D.v2.accent};
	text-decoration: underline;
	cursor: pointer;
	font-family: inherit;
	font-size: inherit;
	display: inline;
	text-align: left;
`;

const MdExternalLink = styled.a`
	color: ${D.v2.accent};
`;

const MdInlineCode = styled.code`
	background: ${D.surface2};
	border: 1px solid ${D.border};
	border-radius: 3px;
	padding: 1px 5px;
	font-family: 'DM Mono', monospace;
	font-size: 0.88em;
	letter-spacing: 0.15px;
	color: ${D.accent};
`;

const MdPre = styled.pre`
	background: ${D.surface};
	border: 1px solid ${D.border};
	border-radius: 6px;
	padding: 10px 12px;
	overflow-x: auto;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
	margin: 8px 0;
`;

const MdPreCode = styled.code`
	font-family: 'DM Mono', monospace;
	color: ${D.textPrimary};
`;

const MdUl = styled.ul`
	margin: 4px 0 8px;
	padding-left: 20px;
`;

const MdOl = styled.ol`
	margin: 4px 0 8px;
	padding-left: 20px;
`;

const MdLi = styled.li`
	${chatBodyFont}
	margin-bottom: 2px;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
`;

const MdH1 = styled.h1`
	${chatHeadingFont}
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.15px;
	margin: 12px 0 6px;
	color: ${D.textPrimary};
`;

const MdH2 = styled.h2`
	${chatHeadingFont}
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
	margin: 10px 0 5px;
	color: ${D.textPrimary};
`;

const MdH3 = styled.h3`
	${chatHeadingFont}
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
	margin: 8px 0 4px;
	color: ${D.textPrimary};
`;

const MdTableWrapper = styled.div`
	overflow-x: auto;
	margin: 8px 0;
	border-radius: 6px;
	border: 1px solid ${D.border};
`;

const MdTable = styled.table`
	border-collapse: collapse;
	width: 100%;
	table-layout: fixed;
`;

/* Detail/1 tokens: 12px, weight 350→500 for headers, letter-spacing 0.3px */
const MdTh = styled.th`
	padding: 6px 10px;
	background: ${D.surface};
	color: ${D.textSecondary};
	text-align: left;
	border-bottom: 1px solid ${D.border};
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.3px;

	&:first-child {
		padding-left: 6px;
	}
`;

const MdTd = styled.td`
	${chatBodyFont}
	padding: 6px 10px;
	border-bottom: 1px solid ${D.border};
	color: ${D.textPrimary};
	font-size: 12px;
	line-height: 16px;
	letter-spacing: 0.3px;
	text-align: left;

	&:first-child {
		padding-left: 6px;
	}
`;

const CellTruncateBtn = styled.button`
	background: none;
	border: none;
	padding: 0;
	color: ${D.v2.accent};
	font-weight: 700;
	cursor: pointer;
	font-family: inherit;
	font-size: 12px;
	line-height: 1.5;
	display: inline;
	vertical-align: middle;
`;

const CellPopover = styled.div`
	position: fixed;
	background: ${D.bg};
	border: 1px solid ${D.border};
	border-radius: 6px;
	padding: 8px 12px;
	z-index: 9999;
	min-width: 160px;
	max-width: 280px;
	word-break: break-word;
	white-space: normal;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	font-size: 12px;
	color: ${D.textPrimary};
`;

const MdStrong = styled.strong`
	font-weight: 500;
`;

const MdBlockquote = styled.blockquote`
	border-left: 3px solid ${D.accentMid};
	margin: 8px 0;
	padding-left: 12px;
	color: ${D.textSecondary};
	font-style: italic;
`;

// ── Message styled components ──────────────────────────────────────────────────

const MessageRow = styled.div<{isUser: boolean}>`
	display: flex;
	flex-direction: column;
	align-items: ${({isUser}) => (isUser ? 'flex-end' : 'flex-start')};
	margin-bottom: 20px;
	animation: ai-fade 0.15s ease-out;
`;

const BubbleWrapper = styled(FlexColumn)`
	width: 332px;
	max-width: 100%;
	position: relative;
`;

// Action-card messages span the full chat-content width (per Figma) and provide their
// own container, so they render outside the BubbleWrapper/Bubble chrome (no double border).
const ActionCardRow = styled.div`
	width: 100%;
`;

const Bubble = styled.div<{isUser: boolean; isError: boolean}>`
	${chatBodyFont}
	padding: 16px;
	border-radius: ${({isUser}) => (isUser ? '8px 2px 8px 8px' : '2px 8px 8px 8px')};
	background: ${({isUser, isError}) => (isError ? D.errorBg : isUser ? D.userBubble : D.assistantBubble)};
	border: 1px solid
		${({isUser, isError}) => (isError ? D.errorBorder : isUser ? D.userBubbleBorder : D.assistantBubbleBorder)};
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.15px;
	color: ${({isUser, isError}) => (isError ? D.errorText : isUser ? '#ffffff' : D.textPrimary)};
	word-break: break-word;
	position: relative;
`;

const CopyButton = styled.button<{copied: boolean}>`
	position: absolute;
	top: 5px;
	right: 5px;
	background: ${({copied}) => (copied ? D.stepDoneBg : D.bg)};
	border: 1px solid ${({copied}) => (copied ? D.stepDoneBorder : D.border)};
	color: ${({copied}) => (copied ? D.stepDoneText : D.textMuted)};
	border-radius: 4px;
	padding: 2px 7px;
	font-size: 10px;
	cursor: pointer;
	font-weight: 500;
`;

const UserText = styled.span`
	${chatBodyFont}
`;

const ExpandToggleWrap = styled.div`
	margin-top: 10px;
`;

const Cursor = styled.span`
	display: inline-block;
	width: 2px;
	height: 12px;
	background: ${D.accentMid};
	margin-left: 2px;
	vertical-align: text-bottom;
	animation: ai-blink 1s step-end infinite;
`;

const StoppedWrapper = styled.span`
	color: ${D.textMuted};
	font-size: 12px;
	font-style: italic;
`;

const StoppedNote = styled.span`
	display: block;
	margin-top: 4px;
	font-size: 11px;
	color: ${D.textMuted};
`;

const Timestamp = styled.div<{isUser: boolean}>`
	font-size: 10px;
	color: ${D.textMuted};
	margin-top: 3px;
	text-align: ${({isUser}) => (isUser ? 'right' : 'left')};
	padding: ${({isUser}) => (isUser ? '0 2px 0 0' : '0 0 0 2px')};
`;

// ── Markdown component overrides ──────────────────────────────────────────────

function extractNodeText(node: React.ReactNode): string | null {
	if (typeof node === 'string') return node;
	if (typeof node === 'number') return String(node);
	if (Array.isArray(node)) {
		const parts = node.map(extractNodeText);
		if (parts.some(p => p === null)) return null;
		return parts.join('');
	}
	if (React.isValidElement(node)) return extractNodeText((node.props as {children?: React.ReactNode}).children);
	return null;
}

/** Native title on a parent does not show when hovering child <a> nodes — attach to links too. */
function applyOverflowTitle(node: React.ReactNode, title: string | undefined): React.ReactNode {
	if (!title) return node;
	if (Array.isArray(node)) {
		return node.map(child => applyOverflowTitle(child, title));
	}
	if (!React.isValidElement(node)) return node;

	const props = node.props as {href?: string; title?: string; children?: React.ReactNode};
	const nextChildren = props.children != null ? applyOverflowTitle(props.children, title) : props.children;

	if (props.href != null && !props.title) {
		return React.cloneElement(node, {title, children: nextChildren});
	}
	if (nextChildren !== props.children) {
		return React.cloneElement(node, {}, nextChildren);
	}
	return node;
}

function TruncatedCellContent({children}: {children: React.ReactNode}) {
	const [overflows, setOverflows] = useState(false);
	const [open, setOpen] = useState(false);
	const [popoverPos, setPopoverPos] = useState<{top: number; left: number} | null>(null);
	const clippedRef = useRef<HTMLSpanElement>(null);
	const btnRef = useRef<HTMLButtonElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	// ResizeObserver fires after the browser finalises table-column widths and again
	// whenever the element's dimensions change (e.g. chat panel shown after being hidden).
	// Compares scrollHeight against (computed lineHeight × 3) rather than clientHeight so
	// subpixel rounding on maxHeight never causes a false overflow signal.
	useEffect(() => {
		const el = clippedRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			const lineHeightPx = parseFloat(window.getComputedStyle(el).lineHeight);
			setOverflows(el.scrollHeight > lineHeightPx * 3 + 0.5);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		if (!overflows) setOpen(false);
	}, [overflows]);

	useEffect(() => {
		if (!open) {
			setPopoverPos(null);
			return;
		}
		if (!btnRef.current) {
			setOpen(false);
			return;
		}
		const r = btnRef.current.getBoundingClientRect();
		const popoverWidth = 280;
		const left = Math.min(r.left, window.innerWidth - popoverWidth - 8);
		setPopoverPos({top: r.bottom + 4, left: Math.max(8, left)});

		let cancelled = false;
		function onMouseDown(e: MouseEvent) {
			if (cancelled) return;
			const target = e.target as Node;
			if (btnRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
			setOpen(false);
		}
		function onKeyDown(e: KeyboardEvent) {
			if (!cancelled && e.key === 'Escape') setOpen(false);
		}
		function onDismiss() {
			if (!cancelled) setOpen(false);
		}
		document.addEventListener('mousedown', onMouseDown);
		document.addEventListener('keydown', onKeyDown);
		window.addEventListener('scroll', onDismiss, true);
		window.addEventListener('resize', onDismiss);
		return () => {
			cancelled = true;
			document.removeEventListener('mousedown', onMouseDown);
			document.removeEventListener('keydown', onKeyDown);
			window.removeEventListener('scroll', onDismiss, true);
			window.removeEventListener('resize', onDismiss);
		};
	}, [open]);

	const text = extractNodeText(children);
	const overflowTitle = overflows && text ? text : undefined;
	const displayChildren = applyOverflowTitle(children, overflowTitle);

	return (
		<span style={{position: 'relative', display: 'block'}} title={overflowTitle}>
			<span
				ref={clippedRef}
				style={{
					display: 'block',
					lineHeight: 1.55,
					maxHeight: 'calc(1.55em * 3)',
					overflow: 'hidden',
					wordBreak: 'break-word',
				}}
			>
				{displayChildren}
			</span>
			{overflows && (
				<CellTruncateBtn
					ref={btnRef}
					onClick={() => setOpen(o => !o)}
					aria-label={open ? 'Close full content' : text ? `Show full name: ${text}` : 'Show full content'}
					aria-expanded={open}
					style={{position: 'absolute', bottom: 0, right: 0, background: D.bg, paddingLeft: '2px'}}
				>
					...
				</CellTruncateBtn>
			)}
			{open &&
				popoverPos &&
				ReactDOM.createPortal(
					<CellPopover ref={popoverRef} style={{top: popoverPos.top, left: popoverPos.left}}>
						{text ?? children}
					</CellPopover>,
					document.body
				)}
		</span>
	);
}

function buildMdComponents(history: HistoryProp) {
	return {
		p({children}: {children: React.ReactNode}) {
			return <MdP>{children}</MdP>;
		},
		a({href, children}: {href?: string; children: React.ReactNode}) {
			if (href && href.startsWith('/')) {
				const normalizedHref = normalizeChatLink(href);
				return (
					<MdInternalLink
						href={normalizedHref}
						onClick={e => {
							if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
							e.preventDefault();
							if (history) history.push(normalizedHref);
						}}
					>
						{children}
					</MdInternalLink>
				);
			}
			return (
				<MdExternalLink href={href} target="_blank" rel="noopener noreferrer">
					{children}
				</MdExternalLink>
			);
		},
		code({inline, children}: {inline?: boolean; children: React.ReactNode}) {
			if (inline) {
				return <MdInlineCode>{children}</MdInlineCode>;
			}
			return (
				<MdPre>
					<MdPreCode>{children}</MdPreCode>
				</MdPre>
			);
		},
		ul({children}: {children: React.ReactNode}) {
			return <MdUl>{children}</MdUl>;
		},
		ol({children}: {children: React.ReactNode}) {
			return <MdOl>{children}</MdOl>;
		},
		li({children}: {children: React.ReactNode}) {
			return <MdLi>{children}</MdLi>;
		},
		h1({children}: {children: React.ReactNode}) {
			return <MdH1>{children}</MdH1>;
		},
		h2({children}: {children: React.ReactNode}) {
			return <MdH2>{children}</MdH2>;
		},
		h3({children}: {children: React.ReactNode}) {
			return <MdH3>{children}</MdH3>;
		},
		table({children}: {children: React.ReactNode}) {
			const cardRows = tryParseStatusCardTable(children);
			if (cardRows) {
				return <StatusCards rows={cardRows} history={history} />;
			}
			return (
				<MdTableWrapper>
					<MdTable>{children}</MdTable>
				</MdTableWrapper>
			);
		},
		th({children}: {children: React.ReactNode}) {
			return <MdTh>{children}</MdTh>;
		},
		td({children, style}: {children: React.ReactNode; style?: React.CSSProperties}) {
			return (
				<MdTd style={{...style, textAlign: 'left'}}>
					<TruncatedCellContent>{children}</TruncatedCellContent>
				</MdTd>
			);
		},
		strong({children}: {children: React.ReactNode}) {
			return <MdStrong>{children}</MdStrong>;
		},
		blockquote({children}: {children: React.ReactNode}) {
			return <MdBlockquote>{children}</MdBlockquote>;
		},
	};
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Cuts at the last newline before maxChars to avoid splitting mid-table-row,
// which causes remark-gfm to emit orphaned text fragments below the table.
function safeSlice(content: string, maxChars: number): string {
	if (content.length <= maxChars) return content;
	const lastNewline = content.lastIndexOf('\n', maxChars);
	return lastNewline > maxChars / 2 ? content.slice(0, lastNewline) : content.slice(0, maxChars);
}

function formatTime(iso: string): string {
	try {
		const d = new Date(iso);
		return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
	} catch (_) {
		return '';
	}
}

// ── Component ─────────────────────────────────────────────────────────────────

type MessageBubbleProps = {
	message: Message;
	isLastAssistant: boolean;
	history: HistoryProp;
	onRespondAction?: (
		actionId: string,
		decision: {approved: boolean; selectedIds: string[]; overrides?: Record<string, Record<string, unknown>>}
	) => Promise<ActionResult>;
	onResumeAction?: (actionId: string) => void;
	onRegenerateAction?: (actionId: string) => void;
	onConfirmReportIntent?: (messageId: string) => void;
	onCancelReportIntent?: (messageId: string) => void;
	onViewReportIntent?: (payload: Record<string, unknown>, messageId: string, savedReportId?: string) => void;
	/** Task-report taxonomy fetched by the chat surface when the report intent arrived; used to
	 * render the report-intent card's condition summary. Optional — a still-in-flight or failed
	 * fetch leaves it undefined and the card falls back to the agent's own summaries/prompt. */
	reportTaxonomy?: ReportTaxonomy;
};

export function MessageBubble({
	message,
	isLastAssistant,
	history,
	onRespondAction,
	onResumeAction,
	onRegenerateAction,
	onConfirmReportIntent,
	onCancelReportIntent,
	onViewReportIntent,
	reportTaxonomy,
}: MessageBubbleProps): JSX.Element {
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const [hovered, setHovered] = useState(false);
	const {formatMessage} = useIntl();

	const mdComponents = useMemo(() => buildMdComponents(history), [history]);

	const isUser = message.role === 'user';
	const isStopped = message.error === 'stopped';
	const isError = !!message.error && !isStopped;
	const normalizedContent = useMemo(() => normalizeMarkdownHeadings(message.content), [message.content]);
	const isLong = !message.pending && !isError && normalizedContent.length > TRUNCATE_AT;
	const displayContent = isLong && !expanded ? safeSlice(normalizedContent, PREVIEW_CHARS) : normalizedContent;
	// Capture in a const so TS narrows it inside the callbacks below (the `&&` guard in JSX
	// does not propagate narrowing into the arrow-function closures). Avoids `!`/`as`.
	const actionPlan = message.actionPlan;

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(message.content).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	}, [message.content]);

	// A report-intent proposal renders as its own confirm card (same full-width, single-border
	// treatment as the write-action ActionCard), not a chat bubble.
	if (message.reportIntent) {
		const reportIntent = message.reportIntent;
		return (
			<MessageRow isUser={false}>
				<ActionCardRow>
					<ReportIntentCard
						payload={reportIntent.payload}
						status={reportIntent.status}
						taxonomy={reportTaxonomy}
						onConfirm={() => onConfirmReportIntent && onConfirmReportIntent(message.id)}
						onCancel={() => onCancelReportIntent && onCancelReportIntent(message.id)}
						onView={() =>
							onViewReportIntent &&
							onViewReportIntent(reportIntent.payload, message.id, reportIntent.savedReportId)
						}
					/>
				</ActionCardRow>
			</MessageRow>
		);
	}

	// A write-action message renders as the ActionCard itself — full chat-content width,
	// its own single-border container, no surrounding chat-bubble chrome.
	if (actionPlan && onRespondAction) {
		return (
			<>
				{/* The plan attaches to the same assistant message the model was streaming
				    into, so rendering only the card discards whatever it had already said —
				    which is often the part that explains the card, or a caveat about the
				    write. Keep that text above the card instead of dropping it. */}
				{normalizedContent.trim().length > 0 && (
					<MessageRow isUser={false}>
						{/* Same chrome as any other assistant bubble: this text is usually the
						    model's answer rather than a one-line preamble, and byte-identical
						    text on a card-less message collapses at PREVIEW_CHARS and offers a
						    copy button. Rendering it raw here made the same content behave
						    differently for no reason the reader can see. */}
						<BubbleWrapper>
							<Bubble
								isUser={false}
								isError={isError}
								data-testid="action-plan-message-text"
								onMouseEnter={() => setHovered(true)}
								onMouseLeave={() => setHovered(false)}
							>
								{!message.pending && hovered && (
									<CopyButton type="button" copied={copied} onClick={handleCopy}>
										{copied
											? formatMessage({id: 'forecast_agent.message.copied'})
											: formatMessage({id: 'forecast_agent.message.copy'})}
									</CopyButton>
								)}
								<ReactMarkdown
									remarkPlugins={[remarkGfm]}
									components={mdComponents as Parameters<typeof ReactMarkdown>[0]['components']}
								>
									{displayContent}
								</ReactMarkdown>
							</Bubble>
							{isLong && (
								<ExpandToggleWrap>
									<MessageExpandToggle
										expanded={expanded}
										onClick={() => setExpanded(prev => !prev)}
										expandLabel={formatMessage({id: 'forecast_agent.message.show_more'})}
										collapseLabel={formatMessage({id: 'forecast_agent.message.show_less'})}
									/>
								</ExpandToggleWrap>
							)}
						</BubbleWrapper>
					</MessageRow>
				)}
				<MessageRow isUser={false}>
					<ActionCardRow>
						<ActionCard
							plan={actionPlan}
							paused={message.actionPaused}
							resolved={message.actionResolved}
							result={message.actionResult}
							dismissed={message.actionDismissed}
							history={history}
							onRespond={decision => onRespondAction(actionPlan.action_id, decision)}
							onResume={() => onResumeAction && onResumeAction(actionPlan.action_id)}
							onRegenerateQuery={() => onRegenerateAction && onRegenerateAction(actionPlan.action_id)}
						/>
					</ActionCardRow>
				</MessageRow>
			</>
		);
	}

	return (
		<MessageRow isUser={isUser}>
			<BubbleWrapper>
				{/* The hover handlers sit on Bubble, not BubbleWrapper: BubbleWrapper is
				    styled(FlexColumn), and FlexColumn destructures a fixed prop list, so any
				    handler passed to it is dropped and never reaches the DOM. */}
				<Bubble
					isUser={isUser}
					isError={isError}
					onMouseEnter={() => setHovered(true)}
					onMouseLeave={() => setHovered(false)}
				>
					{!isUser && !message.pending && message.content && hovered && (
						<CopyButton type="button" copied={copied} onClick={handleCopy}>
							{copied
								? formatMessage({id: 'forecast_agent.message.copied'})
								: formatMessage({id: 'forecast_agent.message.copy'})}
						</CopyButton>
					)}

					{isUser && <UserText>{message.content}</UserText>}

					{!isUser && !isError && !isStopped && (
						<>
							{message.content ? (
								<ReactMarkdown
									remarkPlugins={[remarkGfm]}
									components={mdComponents as Parameters<typeof ReactMarkdown>[0]['components']}
								>
									{displayContent}
								</ReactMarkdown>
							) : null}
							{message.pending && message.content.length === 0 && <ThinkingDots />}
							{message.pending && message.content.length > 0 && <Cursor />}
						</>
					)}

					{isStopped && (
						<StoppedWrapper>
							{message.content ? (
								<>
									<ReactMarkdown
										remarkPlugins={[remarkGfm]}
										components={mdComponents as Parameters<typeof ReactMarkdown>[0]['components']}
									>
										{normalizedContent}
									</ReactMarkdown>
									<StoppedNote>
										<FormattedMessage id="forecast_agent.message.stopped" />
									</StoppedNote>
								</>
							) : (
								<FormattedMessage id="forecast_agent.message.stopped" />
							)}
						</StoppedWrapper>
					)}

					{isError && <span>{message.error}</span>}
				</Bubble>

				{!isUser && !isError && !isStopped && isLong && (
					<ExpandToggleWrap>
						<MessageExpandToggle
							expanded={expanded}
							onClick={() => setExpanded(prev => !prev)}
							expandLabel={formatMessage({id: 'forecast_agent.message.show_more'})}
							collapseLabel={formatMessage({id: 'forecast_agent.message.show_less'})}
						/>
					</ExpandToggleWrap>
				)}

				{message.createdAt && <Timestamp isUser={isUser}>{formatTime(message.createdAt)}</Timestamp>}

				{!isUser && message.statusSteps && message.statusSteps.length > 0 && (
					<StatusSteps steps={message.statusSteps} isActive={isLastAssistant} size="small" />
				)}
			</BubbleWrapper>
		</MessageRow>
	);
}
